import type { ChangeEvent } from "react";
import type { RaRecord } from "react-admin";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { TextInput, useDataProvider } from "react-admin";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

import {
  ITS_DATA_RESOURCE,
  buildItsButtonSearchParams,
  normalizeItsQuery,
} from "./its-lookup-helpers";

export type ItsButtonSearchHandle = {
  search: (targetItsNo?: string) => void;
};

export type ItsButtonSearchFieldProps = {
  source?: string;
  label?: string;
  isRequired?: boolean;
  fullWidth?: boolean;
  helperText?: false | string;
  noResultMessage?: string;
  showNoResult?: boolean;
  onFound: (record: RaRecord) => void;
  onNotFound?: () => void;
  boxSx?: SxProps<Theme>;
  textInputSx?: SxProps<Theme>;
};

const ItsButtonSearchField = forwardRef<ItsButtonSearchHandle, ItsButtonSearchFieldProps>(
  function ItsButtonSearchField(
    {
      source = "itsNo",
      label = "ITS No",
      isRequired,
      fullWidth,
      helperText = false,
      noResultMessage = "ITS Not an HOF or Not Found in Jamaat",
      showNoResult = true,
      onFound,
      onNotFound,
      boxSx,
      textInputSx,
    },
    ref
  ) {
    const dataProvider = useDataProvider();
    const [itsNo, setIts] = useState<string | undefined>();
    const [noResult, setNoResult] = useState(false);
    const [loading, setLoading] = useState(false);

    const search = useCallback(
      (targetItsNo?: string) => {
        const resolvedItsNo = normalizeItsQuery(targetItsNo ?? itsNo);
        if (!resolvedItsNo) return;

        setNoResult(false);
        setLoading(true);
        dataProvider
          .getList(ITS_DATA_RESOURCE, buildItsButtonSearchParams(resolvedItsNo))
          .then(({ data }: { data: RaRecord[] }) => {
            if (data.length > 0) {
              onFound(data[0]);
            } else {
              setNoResult(true);
              onNotFound?.();
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            setLoading(false);
          });
      },
      [dataProvider, itsNo, onFound, onNotFound]
    );

    useImperativeHandle(ref, () => ({ search }), [search]);

    const onChangeDetails = (e: ChangeEvent<HTMLInputElement>) => {
      setIts(e.target.value);
    };

    return (
      <>
        <Box sx={boxSx ?? { display: "flex", alignItems: "flex-start", width: "100%" }}>
          <TextInput
            source={source}
            label={label}
            onChange={(e) => onChangeDetails(e)}
            helperText={helperText}
            fullWidth={fullWidth}
            sx={textInputSx ?? { flex: 1, minWidth: 0, mr: 2, mt: 2 }}
            isRequired={isRequired}
          />
          <Button
            onClick={() => search()}
            variant="contained"
            sx={{ mt: 2, flexShrink: 0 }}
            disabled={loading}
          >
            {!loading ? <SearchIcon /> : <CircularProgress size={20} />}
          </Button>
        </Box>
        {showNoResult && noResult ? (
          <Typography sx={{ my: 3 }}>{noResultMessage}</Typography>
        ) : null}
      </>
    );
  }
);

export default ItsButtonSearchField;
